import { Button, Empty, Select, Spin } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Report } from "notiflix";
import moment from "moment";

import "./styles.css";

import { loginRequest } from "../../../config/api.config";
import { requestToken } from "../../../auth/AuthServices";

import HeaderFarms from "../../../utils/header_farms";
import API from "../../../services";

import TempLayout from "../../../layouts/TempLayout";
import { RibbonCard } from "../../../components/ui/ribbon-card";

const STATUS_VARIANTS = {
  "Waiting Approval": "warning",
  Approved: "success",
  Reject: "error",
  Closed: "success",
  Cancel: "error",
};

const STATUS_OPTIONS = [
  { label: "Waiting Approval", value: "Waiting Approval" },
  { label: "Approved", value: "Approved" },
  { label: "Reject", value: "Reject" },
  { label: "Closed", value: "Closed" },
  { label: "Cancel", value: "Cancel" },
];

const DEFAULT_PER_PAGE = 5;
const DEFAULT_META = {
  page: 1,
  per_page: DEFAULT_PER_PAGE,
  has_more: false,
};

// Dedup-by-id saat append page baru — cegah duplikasi kalau observer fire 2x.
const mergeItems = (current, next) => {
  const map = new Map();
  [...current, ...next].forEach((item) => {
    const key = item?.id_record || item?._id;
    if (key) map.set(key, item);
  });
  return Array.from(map.values());
};

function TAHistory() {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [historyTA, setHistoryTA] = useState([]);
  const [meta, setMeta] = useState(DEFAULT_META);
  const [filter, setFilter] = useState("Waiting Approval");

  const observerTarget = useRef(null);
  const loadingRef = useRef(false);

  const handleLogin = useCallback(() => {
    instance.loginRedirect(loginRequest).catch((e) => {
      console.error(e);
    });
  }, [instance]);

  const fetchPage = useCallback(
    async ({ page = 1, reset = false } = {}) => {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const accessToken = await requestToken();
        if (!accessToken) {
          handleLogin();
          return;
        }

        const res = await API.HistoryTA(HeaderFarms(accessToken), {
          page,
          limit: DEFAULT_PER_PAGE,
          status: filter,
        });

        const nextItems = Array.isArray(res?.data) ? res.data : [];
        const nextMeta = res?.pagination || {};

        setMeta({
          page: nextMeta.page || page,
          per_page: nextMeta.limit || DEFAULT_PER_PAGE,
          has_more: Boolean(nextMeta.hasMore),
        });
        setHistoryTA((current) => (reset ? nextItems : mergeItems(current, nextItems)));
      } catch (err) {
        console.error(err);
        Report.warning(
          "Oops.. something wrong",
          "Sorry, this application is experiencing problems",
          "Okay"
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [filter, handleLogin]
  );

  useEffect(() => {
    if (!isAuthenticated) return;
    setHistoryTA([]);
    setMeta(DEFAULT_META);
    void fetchPage({ page: 1, reset: true });
  }, [fetchPage, isAuthenticated]);

  useEffect(() => {
    const target = observerTarget.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          meta.has_more &&
          !loading &&
          !loadingMore &&
          !loadingRef.current
        ) {
          loadingRef.current = true;
          void fetchPage({ page: meta.page + 1 });
        }
      },
      { threshold: 0.1 }
    );
    if (target) observer.observe(target);
    return () => {
      if (target) observer.unobserve(target);
    };
  }, [fetchPage, loading, loadingMore, meta.has_more, meta.page]);

  useEffect(() => {
    if (!loading && !loadingMore) {
      loadingRef.current = false;
    }
  }, [loading, loadingMore]);

  return (
    <TempLayout title="Travel Authority - History" navNavigateUrl="/home">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <Button
          type="primary"
          ghost
          icon={<PlusOutlined />}
          onClick={() => navigate("/ta/create")}
        >
          New Travel Authority
        </Button>
        <Select
          allowClear
          placeholder="Filter approval status"
          value={filter || undefined}
          options={STATUS_OPTIONS}
          onChange={(v) => setFilter(v || "Waiting Approval")}
          style={{ width: 200 }}
        />
      </div>

      {loading ? (
        <div className="text-center mt-5" style={{ color: "#B7B7B7" }}>
          <Spin size="large" />
        </div>
      ) : historyTA.length > 0 ? (
        <div className="md:space-y-6 space-y-4">
          {historyTA.map((data, index) => {
            const status = data?.approval?.data?.status;
            const variant = STATUS_VARIANTS[status] ?? "black";
            return (
              <Fragment key={data.id_record || index}>
                <RibbonCard
                  position="bottom-right"
                  variant={variant}
                  ribbon={status}
                >
                  <div
                    className="flex flex-col justify-between md:gap-3 gap-2"
                    onClick={() => navigate(`/ta/view/${data.id_record}`)}
                  >
                    <div className="flex flex-col">
                      <p className="md:text-sm text-xs font-semibold">
                        {data.type_travel}
                      </p>
                      <span className="md:text-xs text-[10px] font-medium">
                        Record ID: {data.id_record}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {moment(data.created_at).format("LLL")}
                    </span>
                  </div>
                </RibbonCard>
              </Fragment>
            );
          })}
        </div>
      ) : (
        <Empty
          className="mt-5"
          description={
            <span style={{ color: "#8c8c8c" }}>
              Tidak ada Travel Authority dengan status <b>&quot;{filter}&quot;</b>.
              <br />
              Coba pilih filter lain di kanan atas, atau klik{" "}
              <b>+ New Travel Authority</b> untuk membuat baru.
            </span>
          }
        />
      )}

      <div ref={observerTarget} style={{ height: 20, marginTop: 16 }} />

      {loadingMore && (
        <div className="text-center mt-3" style={{ color: "#B7B7B7" }}>
          <Spin />
        </div>
      )}

      {!loading && !loadingMore && historyTA.length > 0 && !meta.has_more && (
        <p className="text-center mt-4" style={{ color: "#B7B7B7" }}>
          No more data
        </p>
      )}
    </TempLayout>
  );
}

export default TAHistory;
